import { FC, useContext, useMemo, useState } from "react";
import {
  Controller,
  FormProvider,
  useFieldArray,
  useForm,
} from "react-hook-form";
import {
  Alert,
  AlertTitle,
  Box,
  Button,
  Collapse,
  Divider,
  FormControl,
  FormControlLabel,
  FormHelperText,
  InputLabel,
  MenuItem,
  Select,
  Stack,
  Switch,
  Typography,
} from "@mui/material";
import AddIcon from "@mui/icons-material/Add";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import { zodResolver } from "@hookform/resolvers/zod";
import { GlobalModalContext } from "src/utils/GlobalModal/GlobalModalContext";
import __ from "src/utils/Translations";
import getFieldTypeLabel from "src/utils/Company/Absences/getFieldTypeLabel";
import Fieldset from "src/Pages/Company/Absences/Forms/Fieldset";
import { ControlledTextField } from "@benefit-systems/hr-tools-ui";
import ActionButton from "src/Pages/Company/Absences/ActionButton/ActionButton";
import {
  FieldType,
  ICustomField,
} from "src/Pages/Company/Absences/AbsenceType/AbsenceType.models";
import {
  customFieldSchema,
  CustomFieldSchema,
} from "src/Pages/Company/Absences/AbsenceType/AbsenceTypeForm/CustomFieldsConfigurator/CustomFieldSchema";

interface ICustomFieldFormProps {
  field: ICustomField | null;
  fieldId: string | null;
  handleAddCustomField: (
    field: CustomFieldSchema,
    fieldId: string | null
  ) => void;
}

const CustomFieldForm: FC<ICustomFieldFormProps> = ({
  field,
  fieldId = null,
  handleAddCustomField,
}) => {
  const { resetModal } = useContext(GlobalModalContext);
  const [showCancelConfirmation, setShowCancelConfirmation] = useState(false);
  const fieldTypeOptions = useMemo(
    () =>
      Object.values(FieldType).map((value) => ({
        label: getFieldTypeLabel(value),
        value,
      })),
    []
  );

  const formMethods = useForm<CustomFieldSchema>({
    resolver: zodResolver(customFieldSchema),
    mode: "onChange",
    defaultValues: {
      name: field?.name || "",
      fieldType: field?.fieldType || FieldType.TEXT,
      description: field?.description || "",
      required: field?.required || false,
      label: field?.label,
      choices: field?.choices || [
        {
          id: "choice-1",
          value: "",
        },
      ],
    },
  });

  const {
    control,
    handleSubmit,
    watch,
    formState: { errors },
  } = formMethods;

  const chosenFieldType = watch("fieldType");

  const {
    fields: choicesArray,
    remove: removeChoice,
    append: appendChoice,
  } = useFieldArray({
    control,
    name: "choices",
  });

  const onSubmit = (formData: CustomFieldSchema) => {
    handleAddCustomField(formData, fieldId);
  };

  return (
    <FormProvider {...formMethods}>
      <Box
        sx={{
          backgroundColor: "grey.50",
          p: "1.5rem 1rem",
          mb: "1rem",
          borderRadius: "0.5rem",
        }}
        data-testid="custom-field-form"
      >
        <form noValidate onSubmit={handleSubmit(onSubmit)}>
          <Fieldset
            legend={__("Rodzaj pola")}
            description={__("Rozwiń listę, aby wybrać jedną z pozycji:")}
            divider={false}
            required
          >
            <FormControl fullWidth>
              <InputLabel id="fieldType-label">{__("Rodzaj pola")}</InputLabel>
              <Controller
                name="fieldType"
                control={control}
                render={({
                  field: { ref, ...field },
                  fieldState: { invalid },
                }) => (
                  <>
                    <Select
                      {...field}
                      id="fieldType"
                      labelId="fieldType-label"
                      label={__("Typ pola")}
                      fullWidth
                      error={invalid}
                      inputRef={ref}
                      data-testid="custom-field-form-field__type"
                    >
                      {fieldTypeOptions.map(({ value, label }) => (
                        <MenuItem key={value} value={value}>
                          {__(label)}
                        </MenuItem>
                      ))}
                    </Select>
                    <FormHelperText error={!!errors.fieldType}>
                      <>{errors.fieldType?.message}</>
                    </FormHelperText>
                  </>
                )}
              />
            </FormControl>
          </Fieldset>
          <Fieldset legend={__("Nazwa pola")} divider={false} required>
            <ControlledTextField
              name="name"
              label={__("Nazwa pola")}
              maxLength={100}
              required
              data-testid="custom-field-form-field__name"
              normalizeSpaces
            />
          </Fieldset>
          <Fieldset
            legend={__("Podtytuł pola")}
            description={__(
              "Opcjonalnie: Dodaj pomocniczy opis pola, który pojawi się pod jego nazwą i ułatwi użytkownikowi wypełnienie formularza."
            )}
          >
            <ControlledTextField
              name="description"
              label={__("Podtytuł pola")}
              maxLength={255}
              data-testid="custom-field-form-field__description"
            />
          </Fieldset>
          {chosenFieldType === FieldType.CHECKBOX && (
            <Fieldset
              legend={__("Treść pola")}
              required
              description={__(
                `Miejsce do wpisania treści, która pojawi się obok zaznaczenia (checkbox), np. "Tak, wyrażam zgodę."`
              )}
            >
              <ControlledTextField
                name="label"
                label={__("Treść pola")}
                maxLength={255}
                required
                data-testid="custom-field-form-field__label"
              />
            </Fieldset>
          )}
          {chosenFieldType === FieldType.CHOICE_SELECTION && (
            <Fieldset legend={__("Opcje wyboru")} required>
              {choicesArray.map((field, index) => (
                <Stack
                  key={field.id}
                  direction="row"
                  alignItems="center"
                  gap={2}
                  mb="1rem"
                >
                  <ControlledTextField
                    name={`choices.${index}.value`}
                    label={`${__("Nazwa opcji")} ${index + 1}`}
                    maxLength={255}
                    required
                    fullWidth
                    data-testid="custom-field-form-field__choices-value"
                  />
                  <ActionButton
                    label={__("Usuń")}
                    bgColor="red.400"
                    bgColorHover="red.600"
                    color="white"
                    onClick={() => removeChoice(index)}
                    data-testid="custom-field-form-field__choices-delete-button"
                  >
                    <DeleteOutlineIcon />
                  </ActionButton>
                </Stack>
              ))}
              <Button
                variant="text"
                startIcon={<AddIcon />}
                onClick={() =>
                  appendChoice({
                    id: `choice-${choicesArray.length + 1}`,
                    value: "",
                  })
                }
                data-testid="custom-field-form-field__choices-add-button"
              >
                {__("Dodaj")}
              </Button>
              {/* @ts-expect-error zod typing error - it doesn't see types from discriminated union */}
              <FormHelperText error={!!errors.choices}>
                {/* @ts-expect-error zod typing error - up */}
                <>{errors.choices?.message}</>
              </FormHelperText>
            </Fieldset>
          )}
          <Stack
            direction="row"
            justifyContent="space-between"
            alignItems="baseline"
          >
            <Typography variant="subtitle1" component="legend" margin="normal">
              {__("Zarządzanie polem:")}
            </Typography>
            <Controller
              name="required"
              control={control}
              render={({ field: { value, onChange } }) => (
                <FormControlLabel
                  control={
                    <Switch
                      checked={!!value}
                      onChange={onChange}
                      data-testid="custom-field-form-field__required"
                    />
                  }
                  label={__("Pole obowiązkowe")}
                  sx={{
                    flexShrink: 0,
                  }}
                />
              )}
            />
            {chosenFieldType === FieldType.CHOICE_SELECTION && (
              <Controller
                name="multipleChoice"
                control={control}
                render={({ field: { value, onChange } }) => (
                  <FormControlLabel
                    control={
                      <Switch
                        checked={!!value}
                        onChange={onChange}
                        data-testid="custom-field-form-field__multiple-choice"
                      />
                    }
                    label={__("Wielokrotny wybór")}
                    sx={{
                      flexShrink: 0,
                    }}
                  />
                )}
              />
            )}
          </Stack>

          <Divider sx={{ margin: "1rem 0" }} />
          <Alert severity="info" sx={{ mb: "2.5rem" }}>
            {__(
              "Zapisanie tego pola nie jest równoznaczne ze złożeniem wniosku. Aby go złożyć, zakończ wypełnianie formularza."
            )}
          </Alert>
          <Box
            sx={{
              textAlign: "right",
            }}
          >
            <Button
              variant="outlined"
              type="button"
              size="large"
              sx={{ marginRight: "1rem" }}
              onClick={() => setShowCancelConfirmation(true)}
            >
              {__("Anuluj")}
            </Button>
            <Button
              variant="contained"
              type="submit"
              size="large"
              disabled={!chosenFieldType}
              data-testid="custom-field-form-submit-button"
            >
              {__("Zapisz i kontynuuj tworzenie wniosku")}
            </Button>
          </Box>
        </form>
        <Collapse in={showCancelConfirmation}>
          <Alert
            severity="warning"
            sx={{ flexWrap: "wrap", mt: 2, mb: 2 }}
            action={
              <Box sx={{ display: "flex", gap: 1 }}>
                <Button
                  color="inherit"
                  size="small"
                  onClick={() => setShowCancelConfirmation(false)}
                >
                  {__("Nie, zostań")}
                </Button>
                <Button
                  color="warning"
                  size="small"
                  variant="contained"
                  onClick={resetModal}
                >
                  {__("Tak, anuluj")}
                </Button>
              </Box>
            }
          >
            <AlertTitle>{__("Czy na pewno chcesz anulować?")}</AlertTitle>
            {__(
              "Opuszczenie tego formularza spowoduje utratę wprowadzonych zmian."
            )}
          </Alert>
        </Collapse>
      </Box>
    </FormProvider>
  );
};

export default CustomFieldForm;
